/**
 * Centralized debugging service for the Outlook Add-in
 * Controls console logging based on environment variables from environments.json
 *
 * IMPORTANT: This service reads from runtime config, so it will respect
 * the DEBUG_ENABLED and DEBUG_LEVEL settings from the loaded environment.
 */
import { environment } from '../config/environment';
import runtimeConfig from '../config/runtimeConfig';
export class DebugService {
    // Environment variable to control debug logging - read dynamically from runtime config
    get DEBUG_ENABLED() {
        // Always allow reading the config value, even if runtime config isn't initialized yet
        // This allows DebugService to work during initial load
        return environment.DEBUG_ENABLED;
    }
    get DEBUG_LEVEL() {
        return environment.DEBUG_LEVEL;
    }
    constructor() {
        // Debug levels
        this.DEBUG_LEVELS = {
            error: 0,
            warn: 1,
            info: 2,
            debug: 3,
            trace: 4
        };
        // Don't log during construction - wait until runtime config is loaded
        // The first log call will check the config and respect DEBUG_ENABLED
    }
    /**
     * Logs initialization info only if debug is enabled
     * Call this after runtime config is loaded
     */
    logInitialization() {
        if (this.DEBUG_ENABLED) {
            console.log('🔧 DebugService initialized:', {
                enabled: this.DEBUG_ENABLED,
                level: this.DEBUG_LEVEL,
                currentLevel: this.DEBUG_LEVELS[this.DEBUG_LEVEL] || 2,
                runtimeConfigInitialized: runtimeConfig.isInitialized(),
                environment: runtimeConfig.isInitialized() ? runtimeConfig.getEnvironment() : 'not loaded'
            });
        }
    }
    static getInstance() {
        if (!DebugService.instance) {
            DebugService.instance = new DebugService();
        }
        return DebugService.instance;
    }
    /**
     * Check if debug logging is enabled
     */
    isEnabled() {
        return this.DEBUG_ENABLED;
    }
    /**
     * Check if a specific debug level should be logged
     */
    shouldLog(level) {
        // Errors should always be logged, even if DEBUG_ENABLED is false
        if (level === 'error') {
            return true;
        }
        // For all other levels, check DEBUG_ENABLED
        if (!this.DEBUG_ENABLED)
            return false;
        const currentLevel = this.DEBUG_LEVELS[this.DEBUG_LEVEL] || 2;
        const messageLevel = this.DEBUG_LEVELS[level] || 2;
        return messageLevel <= currentLevel;
    }
    /**
     * Log error messages
     * Errors are always logged, even if DEBUG_ENABLED is false
     */
    error(message, ...args) {
        // Always log errors - they're important regardless of debug setting
        console.error(`🔴 [ERROR] ${message}`, ...args);
    }
    /**
     * Log warning messages
     */
    warn(message, ...args) {
        if (this.shouldLog('warn')) {
            console.warn(`🟡 [WARN] ${message}`, ...args);
        }
    }
    /**
     * Log info messages
     */
    info(message, ...args) {
        if (this.shouldLog('info')) {
            console.log(`🔵 [INFO] ${message}`, ...args);
        }
    }
    /**
     * Log debug messages
     */
    debug(message, ...args) {
        if (this.shouldLog('debug')) {
            console.log(`🟢 [DEBUG] ${message}`, ...args);
        }
    }
    /**
     * Log trace messages (most verbose)
     */
    trace(message, ...args) {
        if (this.shouldLog('trace')) {
            console.log(`⚪ [TRACE] ${message}`, ...args);
        }
    }
    /**
     * Log with custom prefix
     */
    log(prefix, message, ...args) {
        if (this.shouldLog('info')) {
            console.log(`📝 [${prefix}] ${message}`, ...args);
        }
    }
    /**
     * Log section headers for better organization
     */
    section(title) {
        if (this.shouldLog('info')) {
            console.log(`\n${'='.repeat(50)}`);
            console.log(`📋 ${title}`);
            console.log(`${'='.repeat(50)}`);
        }
    }
    /**
     * Log subsection headers
     */
    subsection(title) {
        if (this.shouldLog('info')) {
            console.log(`\n${'-'.repeat(30)}`);
            console.log(`📌 ${title}`);
            console.log(`${'-'.repeat(30)}`);
        }
    }
    /**
     * Log object data in a formatted way
     */
    object(label, obj) {
        if (this.shouldLog('debug')) {
            console.log(`📊 [OBJECT] ${label}:`, obj);
        }
    }
    /**
     * Log API calls
     */
    api(method, url, data) {
        if (this.shouldLog('debug')) {
            console.log(`🌐 [API] ${method} ${url}`, data ? { data } : '');
        }
    }
    /**
     * Log authentication events
     */
    auth(event, details) {
        if (this.shouldLog('info')) {
            console.log(`🔐 [AUTH] ${event}`, details || '');
        }
    }
    /**
     * Log email operations
     */
    email(operation, details) {
        if (this.shouldLog('info')) {
            console.log(`📧 [EMAIL] ${operation}`, details || '');
        }
    }
    /**
     * Log placement operations
     */
    placement(operation, details) {
        if (this.shouldLog('info')) {
            console.log(`📦 [PLACEMENT] ${operation}`, details || '');
        }
    }
    /**
     * Log Graph API operations
     */
    graph(operation, details) {
        if (this.shouldLog('debug')) {
            console.log(`📈 [GRAPH] ${operation}`, details || '');
        }
    }
    /**
     * Log service operations
     */
    service(serviceName, operation, details) {
        if (this.shouldLog('debug')) {
            console.log(`⚙️ [${serviceName.toUpperCase()}] ${operation}`, details || '');
        }
    }
    /**
     * Log timing information
     */
    timing(operation, startTime) {
        if (this.shouldLog('debug')) {
            const duration = Date.now() - startTime;
            console.log(`⏱️ [TIMING] ${operation} took ${duration}ms`);
        }
    }
    /**
     * Log error with stack trace
     */
    errorWithStack(message, error) {
        if (this.shouldLog('error')) {
            console.error(`🔴 [ERROR] ${message}:`, error);
            if (error.stack) {
                console.error(`🔴 [STACK] ${error.stack}`);
            }
        }
    }
    /**
     * Log performance metrics
     */
    performance(metric, value, unit = 'ms') {
        if (this.shouldLog('debug')) {
            console.log(`📊 [PERF] ${metric}: ${value}${unit}`);
        }
    }
}
// Export singleton instance
export default DebugService.getInstance();
